import { defineStore } from 'pinia';
import { classifiedOrderApi } from '@/api/classifiedOrderApi';
import {
  getAllowedGroupClassificationKeys,
  getBrowseDaysBeforeToday,
  getBrowseDaysAfterToday,
  getBrowsePageSize,
  getBrowsePageThreshold,
  getBrowseGroupOverrides,
} from '@/api/config';
import type {
  ClassificationInfo,
  ClassificationTree,
  TreeNode,
  ScheduledAdInfo,
  ScheduledRetailClassDisplayAdInfo,
  ClassificationAdCount,
  CustomFieldInfo,
} from '@/types/classifiedOrder';

/**
 * Represents a classification with its ads
 */
export interface ClassificationWithAds {
  classificationKey: number;
  name: string;
  displayAds: ScheduledRetailClassDisplayAdInfo[]; // Business image ads (shown first)
  regularAds: ScheduledAdInfo[]; // Regular classified ads
  totalAdCount: number;
}

/**
 * A single query-scoped page of regular classified listings.
 * Search pages are intentionally not written to the canonical unfiltered cache.
 */
export interface ClassificationSearchPageResult {
  classificationKey: number;
  searchText: string;
  regularAds: ScheduledAdInfo[];
  totalCount: number;
  skip: number;
  take: number;
  nextSkip: number;
  hasMore: boolean;
}

/**
 * Represents a classification with counts only (for sidebar display)
 */
export interface ClassificationWithCounts {
  classificationKey: number;
  name: string;
  regularAdCount: number;
  displayAdCount: number;
  totalAdCount: number;
}

/**
 * Represents a subgroup (intermediate level) with its leaf classifications
 * Used for 3-level hierarchies: Group > Subgroup > Leaf
 */
export interface SubgroupWithAds {
  classificationKey: number;
  name: string;
  startingNumber: number;
  endingNumber: number;
  classifications: ClassificationWithCounts[];
  totalAdCount: number;
}

/**
 * Represents a category group with its child classifications and optional subgroups
 * Supports both 2-level (Group > Leaf) and 3-level (Group > Subgroup > Leaf) hierarchies
 */
export interface CategoryGroupWithAds {
  classificationKey: number;
  name: string;
  startingNumber: number;
  endingNumber: number;
  classifications: ClassificationWithCounts[];  // Direct leaf children
  subgroups: SubgroupWithAds[];  // Nested subgroups with their leaves (for 3-level hierarchy)
  totalAdCount: number;
}

/**
 * Cached ads for a classification
 */
interface CachedClassificationAds {
  classificationKey: number;
  regularAds: ScheduledAdInfo[];
  displayAds: ScheduledRetailClassDisplayAdInfo[];
  searchText: string;
  fetchedAt: number; // timestamp
}

/**
 * Track in-progress fetch promises to avoid duplicate API calls.
 * This is outside the store state because Promises don't serialize well.
 */
interface PendingClassificationFetch {
  promise: Promise<CachedClassificationAds | null>;
  epoch: number;
  revision: number;
  searchText: string;
}

interface PendingCustomFieldFetch {
  promise: Promise<CustomFieldInfo[]>;
  epoch: number;
}

interface PendingPaginationFetch {
  promise: Promise<void>;
  epoch: number;
  revision: number;
  searchText: string;
  skip: number;
}

const pendingFetches = new Map<number, PendingClassificationFetch>();
const pendingCustomFieldFetches = new Map<number, PendingCustomFieldFetch>();
const pendingPaginationFetches = new Map<number, PendingPaginationFetch>();
const cacheRevisions = new Map<number, number>();
let cacheEpoch = 0;
let countsGeneration = 0;

function getCacheRevision(classificationKey: number): number {
  return cacheRevisions.get(classificationKey) ?? 0;
}

function isCurrentCacheRequest(classificationKey: number, epoch: number, revision: number): boolean {
  return cacheEpoch === epoch && getCacheRevision(classificationKey) === revision;
}

/**
 * Return whether classification metadata identifies an exact selectable leaf.
 * Group nodes always have an ending number; leaves have no ending number.
 */
function isLeafClassificationInfo(info: ClassificationInfo | null | undefined): info is ClassificationInfo {
  return !!info && (info.endingNumber === null || info.endingNumber === undefined);
}

/**
 * Store state interface
 */
interface BrowseListingsState {
  // Classification tree from API
  classificationTree: ClassificationTree | null;
  
  // Counts per classification (from optimized COUNT query)
  counts: Map<number, ClassificationAdCount>;
  
  // Cached ads per classification (lazy loaded)
  adCache: Map<number, CachedClassificationAds>;
  
  // Cached custom field definitions per classification
  customFieldsCache: Map<number, CustomFieldInfo[]>;
  
  // Currently loading classification key (null if not loading)
  loadingClassificationKey: number | null;
  
  // Background prefetching state
  isPrefetching: boolean;
  prefetchQueue: number[];
  prefetchGeneration: number;
  
  // UI state
  loading: boolean;
  error: string | null;
  lastFetchDate: string | null;
  
  // Date range parameters used for fetching
  daysBeforeToday: number;
  daysAfterToday: number;

  // Pagination state per classification
  paginationState: Map<number, {
    totalCount: number;
    loadedCount: number;
    hasMore: boolean;
  }>;

  // Current search text (applies to all classifications)
  currentSearchText: string;
  isSearching: boolean;
}

export const useBrowseListingsStore = defineStore('browseListings', {
  state: (): BrowseListingsState => ({
    classificationTree: null,
    counts: new Map<number, ClassificationAdCount>(),
    adCache: new Map<number, CachedClassificationAds>(),
    customFieldsCache: new Map<number, CustomFieldInfo[]>(),
    loadingClassificationKey: null,
    isPrefetching: false,
    prefetchQueue: [],
    prefetchGeneration: 0,
    loading: false,
    error: null,
    lastFetchDate: null,
    daysBeforeToday: 6,
    daysAfterToday: 7,
    paginationState: new Map(),
    currentSearchText: '',
    isSearching: false,
  }),

  getters: {
    /**
     * Build a flat map of classificationKey -> classification info from the tree
     */
    classificationMap(): Map<number, { info: ClassificationInfo; parent: ClassificationInfo | null }> {
      const map = new Map<number, { info: ClassificationInfo; parent: ClassificationInfo | null }>();
      
      const traverse = (node: TreeNode, parent: ClassificationInfo | null) => {
        map.set(node.data.classificationKey, { info: node.data, parent });
        for (const child of node.children) {
          traverse(child, node.data);
        }
      };
      
      if (this.classificationTree) {
        for (const root of this.classificationTree.roots) {
          traverse(root, null);
        }
      }
      
      return map;
    },

    /**
     * Build the category groups with counts hierarchy using the tree structure.
     * Supports both 2-level (Group > Leaf) and 3-level (Group > Subgroup > Leaf) hierarchies.
     * Filters by allowed group classification keys if configured for the site.
     */
    categoryGroupsWithAds(): CategoryGroupWithAds[] {
      const countsMap = this.counts;
      const tree = this.classificationTree;
      
      if (!tree || !tree.roots) {
        return [];
      }
      
      // Get allowed group classification keys from site config (null = all allowed)
      const allowedGroups = getAllowedGroupClassificationKeys();
      const allowedGroupsSet = allowedGroups ? new Set(allowedGroups) : null;
      
      // Helper to check if a node is a leaf (no ending number means it's a leaf)
      const isLeafNode = (node: TreeNode): boolean => {
        return isLeafClassificationInfo(node.data);
      };
      
      // Helper to get counts for a classification
      const getCountsForKey = (key: number): ClassificationWithCounts | null => {
        const countInfo = countsMap.get(key);
        if (!countInfo) return null;
        
        const regularAdCount = countInfo.regularAdCount;
        const displayAdCount = countInfo.displayAdCount;
        const totalAdCount = regularAdCount + displayAdCount;
        
        if (totalAdCount === 0) return null;
        
        // Get name from tree - need to find the node
        let name = `Classification ${key}`;
        const findName = (nodes: TreeNode[]): string | null => {
          for (const node of nodes) {
            if (node.data.classificationKey === key) {
              return node.data.name;
            }
            const found = findName(node.children);
            if (found) return found;
          }
          return null;
        };
        const foundName = findName(tree.roots);
        if (foundName) name = foundName;
        
        return {
          classificationKey: key,
          name,
          regularAdCount,
          displayAdCount,
          totalAdCount,
        };
      };
      
      // Build category groups from tree roots
      const categoryGroups: CategoryGroupWithAds[] = [];
      
      for (const rootNode of tree.roots) {
        // Filter by allowed group classification keys if configured
        if (allowedGroupsSet && !allowedGroupsSet.has(rootNode.data.classificationKey)) {
          continue;
        }
        
        const directLeaves: ClassificationWithCounts[] = [];
        const subgroups: SubgroupWithAds[] = [];
        
        // Process children of the root (level 2)
        for (const childNode of rootNode.children) {
          if (isLeafNode(childNode)) {
            // Direct leaf classification under root
            const counts = getCountsForKey(childNode.data.classificationKey);
            if (counts) {
              directLeaves.push(counts);
            }
          } else {
            // This is a subgroup - process its children (level 3)
            const subgroupLeaves: ClassificationWithCounts[] = [];
            
            // Recursively collect all leaf descendants of this subgroup
            const collectLeaves = (node: TreeNode) => {
              for (const child of node.children) {
                if (isLeafNode(child)) {
                  const counts = getCountsForKey(child.data.classificationKey);
                  if (counts) {
                    subgroupLeaves.push(counts);
                  }
                } else {
                  // Nested subgroup - recurse deeper
                  collectLeaves(child);
                }
              }
            };
            collectLeaves(childNode);
            
            // Only include subgroup if it has leaves with ads
            if (subgroupLeaves.length > 0) {
              subgroupLeaves.sort((a, b) => a.name.localeCompare(b.name));
              const subgroupTotal = subgroupLeaves.reduce((sum, c) => sum + c.totalAdCount, 0);
              
              subgroups.push({
                classificationKey: childNode.data.classificationKey,
                name: childNode.data.name,
                startingNumber: childNode.data.startingNumber,
                endingNumber: childNode.data.endingNumber || 0,
                classifications: subgroupLeaves,
                totalAdCount: subgroupTotal,
              });
            }
          }
        }
        
        // Only include root group if it has any content (direct leaves or subgroups with leaves)
        if (directLeaves.length > 0 || subgroups.length > 0) {
          directLeaves.sort((a, b) => a.name.localeCompare(b.name));
          subgroups.sort((a, b) => a.name.localeCompare(b.name));
          
          const totalFromLeaves = directLeaves.reduce((sum, c) => sum + c.totalAdCount, 0);
          const totalFromSubgroups = subgroups.reduce((sum, s) => sum + s.totalAdCount, 0);
          
          categoryGroups.push({
            classificationKey: rootNode.data.classificationKey,
            name: rootNode.data.name,
            startingNumber: rootNode.data.startingNumber,
            endingNumber: rootNode.data.endingNumber || 0,
            classifications: directLeaves,
            subgroups: subgroups,
            totalAdCount: totalFromLeaves + totalFromSubgroups,
          });
        }
      }
      
      categoryGroups.sort((a, b) => a.name.localeCompare(b.name));
      return categoryGroups;
    },

    /**
     * Total number of ads across all categories
     */
    totalAdCount(): number {
      let total = 0;
      for (const count of this.counts.values()) {
        total += count.regularAdCount + count.displayAdCount;
      }
      return total;
    },

    /**
     * Check if there are any ads to display
     */
    hasAds(): boolean {
      return this.totalAdCount > 0;
    },

    /**
     * Check if currently loading a specific classification
     */
    loadingClassification(): number | null {
      return this.loadingClassificationKey;
    },

    /**
     * Get custom field definitions for a classification.
     * Returns empty array if not cached.
     */
    getCustomFieldsForClassification() {
      return (classificationKey: number): CustomFieldInfo[] => {
        return this.customFieldsCache.get(classificationKey) || [];
      };
    },

    /**
     * Get a specific custom field info by classification and field key.
     * Returns undefined if not found.
     */
    getCustomFieldInfo() {
      return (classificationKey: number, fieldKey: number): CustomFieldInfo | undefined => {
        const fields = this.customFieldsCache.get(classificationKey);
        if (!fields) return undefined;
        return fields.find(f => f.customFieldKey === fieldKey);
      };
    },
  },

  actions: {
    /**
     * Fetch only counts and classification tree for fast initial page load.
     * Uses optimized COUNT queries on the backend.
     */
    async fetchCounts() {
      const generation = ++countsGeneration;
      this.loading = true;
      this.error = null;

      // Prevent requests started against the previous classification/count snapshot
      // from repopulating caches after this refresh begins.
      cacheEpoch += 1;
      cacheRevisions.clear();
      pendingFetches.clear();
      pendingCustomFieldFetches.clear();
      pendingPaginationFetches.clear();
      this.stopPrefetch();

      const defaultDaysBefore = getBrowseDaysBeforeToday();
      const defaultDaysAfter = getBrowseDaysAfterToday();
      this.daysBeforeToday = defaultDaysBefore;
      this.daysAfterToday = defaultDaysAfter;

      try {
        // Fetch classification tree first (sequential to avoid backend DbContext issues)
        console.log('[BrowseListings] Fetching classification tree...');
        const classificationTree = await classifiedOrderApi.getClassificationsTree();
        console.log('[BrowseListings] Classification tree roots:', classificationTree.roots?.length);
        if (generation !== countsGeneration) return;

        // Build list of count fetches: default + one per unique override date range
        const groupOverrides = getBrowseGroupOverrides();
        const overrideMap = new Map<string, { daysBefore: number; daysAfter: number; groupKeys: number[] }>();

        if (groupOverrides && groupOverrides.length > 0) {
          for (const override of groupOverrides) {
            const key = `${override.daysBeforeToday}_${override.daysAfterToday}`;
            if (!overrideMap.has(key)) {
              overrideMap.set(key, {
                daysBefore: override.daysBeforeToday,
                daysAfter: override.daysAfterToday,
                groupKeys: [],
              });
            }
            overrideMap.get(key)!.groupKeys.push(...override.groupClassificationKeys);
          }
        }

        // Collect all overridden group keys
        const overriddenGroupKeys = new Set<number>();
        for (const o of overrideMap.values()) {
          o.groupKeys.forEach(k => overriddenGroupKeys.add(k));
        }

        // Fire parallel count queries
        const allGroupKeys = getAllowedGroupClassificationKeys();
        type CountResult = { counts: ClassificationAdCount[] };
        const fetchPromises: Promise<CountResult>[] = [];

        // Default range query (all classifications)
        console.log('[BrowseListings] Fetching browse counts...');
        fetchPromises.push(
          classifiedOrderApi.getBrowseCounts(defaultDaysBefore, defaultDaysAfter)
            .then(r => ({ counts: r?.counts || [] }))
        );

        // Override range queries (one per unique date range, scoped to those group keys)
        for (const [, override] of overrideMap) {
          const effectiveGroupKeys = allGroupKeys
            ? override.groupKeys.filter(k => allGroupKeys.includes(k))
            : override.groupKeys;
          if (effectiveGroupKeys.length === 0) continue;

          console.log(`[BrowseListings] Fetching override counts for groups [${effectiveGroupKeys}] with ${override.daysBefore}/${override.daysAfter} days`);
          fetchPromises.push(
            classifiedOrderApi.getBrowseCounts(override.daysBefore, override.daysAfter, effectiveGroupKeys)
              .then(r => ({ counts: r?.counts || [] }))
          );
        }

        const results = await Promise.all(fetchPromises);
        if (generation !== countsGeneration) return;

        // Store classification tree
        this.classificationTree = classificationTree;

        // Merge counts: default first, then override results replace matching classifications
        this.counts = new Map<number, ClassificationAdCount>();

        // Add default counts
        for (const count of results[0].counts) {
          if (count && count.classificationKey !== undefined) {
            this.counts.set(count.classificationKey, count);
          }
        }

        // Override counts from wider-range queries
        for (let i = 1; i < results.length; i++) {
          for (const count of results[i].counts) {
            if (count && count.classificationKey !== undefined) {
              this.counts.set(count.classificationKey, count);
            }
          }
        }

        console.log('[BrowseListings] Total classifications with counts:', this.counts.size);

        // Clear caches when counts are refreshed
        this.adCache = new Map<number, CachedClassificationAds>();
        this.customFieldsCache = new Map<number, CustomFieldInfo[]>();
        this.paginationState = new Map();
        this.currentSearchText = '';
        this.isSearching = false;

        this.lastFetchDate = new Date().toISOString().split('T')[0];

      } catch (err) {
        if (generation !== countsGeneration) return;
        console.error('Failed to fetch counts:', err);
        this.error = err instanceof Error ? err.message : 'Failed to load classification counts';
      } finally {
        if (generation === countsGeneration) {
          this.loading = false;
        }
      }
    },

    /**
     * Check whether a classification key resolves to an exact leaf in the current tree.
     * Unknown keys and group/range nodes are rejected so they cannot reach leaf-only endpoints.
     * @param classificationKey Classification key to validate.
     * @returns True only for an exact leaf classification in the loaded tree.
     */
    isLeafClassificationKey(classificationKey: number): boolean {
      return isLeafClassificationInfo(this.classificationMap.get(classificationKey)?.info);
    },

    /**
     * Invalidate canonical listings and pagination for one classification.
     * Any already-running request may finish, but its revision can no longer commit.
     * @param classificationKey Classification whose canonical cache should be invalidated.
     */
    invalidateClassificationCache(classificationKey: number) {
      cacheRevisions.set(classificationKey, getCacheRevision(classificationKey) + 1);
      this.adCache.delete(classificationKey);
      this.paginationState.delete(classificationKey);
      pendingFetches.delete(classificationKey);
      pendingPaginationFetches.delete(classificationKey);

      if (this.loadingClassificationKey === classificationKey) {
        this.loadingClassificationKey = null;
        this.isSearching = false;
      }
    },

    /**
     * Ensure custom-field definitions have been requested for a classification.
     * Successful empty results are cached; failures are not cached so a later call can retry.
     * @param classificationKey Classification whose field definitions are required.
     * @returns Cached or freshly loaded custom-field definitions, or an empty array on failure.
     */
    async ensureCustomFieldsForClassification(classificationKey: number): Promise<CustomFieldInfo[]> {
      if (!this.isLeafClassificationKey(classificationKey)) {
        console.warn(`[BrowseListings] Skipping custom fields for non-leaf classification ${classificationKey}.`);
        return [];
      }

      if (this.customFieldsCache.has(classificationKey)) {
        return this.customFieldsCache.get(classificationKey)!;
      }

      const existing = pendingCustomFieldFetches.get(classificationKey);
      if (existing && existing.epoch === cacheEpoch) {
        return existing.promise;
      }
      if (existing) {
        pendingCustomFieldFetches.delete(classificationKey);
      }

      const epoch = cacheEpoch;
      const promise = classifiedOrderApi.getCustomFields(classificationKey)
        .then(fields => {
          const normalizedFields = Array.isArray(fields) ? fields : [];
          if (cacheEpoch === epoch) {
            this.customFieldsCache.set(classificationKey, normalizedFields);
          }
          return normalizedFields;
        })
        .catch(err => {
          console.warn(`[BrowseListings] Failed to fetch custom fields for ${classificationKey}:`, err);
          return [] as CustomFieldInfo[];
        })
        .finally(() => {
          const current = pendingCustomFieldFetches.get(classificationKey);
          if (current?.promise === promise) {
            pendingCustomFieldFetches.delete(classificationKey);
          }
        });

      pendingCustomFieldFetches.set(classificationKey, { promise, epoch });
      return promise;
    },

    /**
     * Fetch unfiltered ads for a specific classification (lazy loading).
     * Uses canonical cached data or waits for a compatible in-progress request.
     * @param classificationKey Leaf classification to load.
     * @param trackLoading Whether to expose this request through the legacy scalar loading state.
     * @returns The classification listings, or null when the request fails or is invalidated.
     */
    async fetchAdsForClassification(
      classificationKey: number,
      trackLoading = true,
    ): Promise<ClassificationWithAds | null> {
      if (!this.isLeafClassificationKey(classificationKey)) {
        console.warn(`[BrowseListings] Skipping listings request for non-leaf classification ${classificationKey}.`);
        return null;
      }

      let cached = this.adCache.get(classificationKey);
      if (cached?.searchText) {
        // A disabled-mode legacy search owns this query-scoped cache entry. A canonical
        // request must invalidate it rather than expose filtered data as unfiltered data.
        this.invalidateClassificationCache(classificationKey);
        cached = undefined;
      }
      if (cached) {
        console.log(`[BrowseListings] Using cached ads for classification ${classificationKey}`);
        const cachedEpoch = cacheEpoch;
        const cachedRevision = getCacheRevision(classificationKey);
        await this.ensureCustomFieldsForClassification(classificationKey);
        if (!isCurrentCacheRequest(classificationKey, cachedEpoch, cachedRevision)) {
          return null;
        }
        cached = this.adCache.get(classificationKey);
        if (cached && !cached.searchText) {
          const classInfo = this.classificationMap.get(classificationKey);
          return {
            classificationKey,
            name: classInfo?.info.name || `Classification ${classificationKey}`,
            displayAds: cached.displayAds,
            regularAds: cached.regularAds,
            totalAdCount: cached.regularAds.length + cached.displayAds.length,
          };
        }
      }

      let epoch = cacheEpoch;
      let revision = getCacheRevision(classificationKey);
      let pending = pendingFetches.get(classificationKey);
      if (pending && (pending.epoch !== epoch || pending.revision !== revision)) {
        pendingFetches.delete(classificationKey);
        pending = undefined;
      }
      if (pending?.searchText) {
        // Do not allow an in-flight legacy search to satisfy or later overwrite an
        // unfiltered request for the same classification.
        this.invalidateClassificationCache(classificationKey);
        epoch = cacheEpoch;
        revision = getCacheRevision(classificationKey);
        pending = undefined;
      }

      if (pending) {
        console.log(`[BrowseListings] Waiting for in-progress fetch for classification ${classificationKey}...`);
        if (trackLoading) this.loadingClassificationKey = classificationKey;
        try {
          const result = await pending.promise;
          if (!result || !isCurrentCacheRequest(classificationKey, pending.epoch, pending.revision)) {
            return null;
          }

          const classInfo = this.classificationMap.get(classificationKey);
          return {
            classificationKey,
            name: classInfo?.info.name || `Classification ${classificationKey}`,
            displayAds: result.displayAds,
            regularAds: result.regularAds,
            totalAdCount: result.regularAds.length + result.displayAds.length,
          };
        } finally {
          if (trackLoading && this.loadingClassificationKey === classificationKey) {
            this.loadingClassificationKey = null;
          }
        }
      }

      if (trackLoading) this.loadingClassificationKey = classificationKey;
      const fetchPromise = this.doFetchClassificationAds(classificationKey, epoch, revision);
      pendingFetches.set(classificationKey, {
        promise: fetchPromise,
        epoch,
        revision,
        searchText: '',
      });

      try {
        const result = await fetchPromise;
        if (!result || !isCurrentCacheRequest(classificationKey, epoch, revision)) {
          return null;
        }

        const classInfo = this.classificationMap.get(classificationKey);
        return {
          classificationKey,
          name: classInfo?.info.name || `Classification ${classificationKey}`,
          displayAds: result.displayAds,
          regularAds: result.regularAds,
          totalAdCount: result.regularAds.length + result.displayAds.length,
        };
      } finally {
        if (trackLoading && this.loadingClassificationKey === classificationKey) {
          this.loadingClassificationKey = null;
        }
        const current = pendingFetches.get(classificationKey);
        if (current?.promise === fetchPromise) {
          pendingFetches.delete(classificationKey);
        }
      }
    },

    /**
     * Get the effective date range for a classification by checking group overrides.
     * Walks up the tree to find the root group, then checks for matching overrides.
     */
    getEffectiveDateRange(classificationKey: number): { daysBefore: number; daysAfter: number } {
      const defaultRange = {
        daysBefore: getBrowseDaysBeforeToday(),
        daysAfter: getBrowseDaysAfterToday(),
      };

      const groupOverrides = getBrowseGroupOverrides();
      if (!groupOverrides || groupOverrides.length === 0) return defaultRange;

      // Find the root group for this classification
      const tree = this.classificationTree;
      if (!tree) return defaultRange;

      let groupKey: number | null = null;

      // Check if this IS a root node
      for (const root of tree.roots) {
        if (root.data.classificationKey === classificationKey) {
          groupKey = classificationKey;
          break;
        }
      }

      // Walk up via classificationMap to find the root
      if (groupKey === null) {
        const classEntry = this.classificationMap.get(classificationKey);
        if (classEntry) {
          let current = classEntry;
          while (current.parent) {
            // Check if parent is a root
            for (const root of tree.roots) {
              if (root.data.classificationKey === current.parent.classificationKey) {
                groupKey = current.parent.classificationKey;
                break;
              }
            }
            if (groupKey !== null) break;
            const parentEntry = this.classificationMap.get(current.parent.classificationKey);
            if (!parentEntry) break;
            current = parentEntry;
          }
        }
      }

      if (groupKey === null) return defaultRange;

      // Check if this group has an override
      for (const override of groupOverrides) {
        if (override.groupClassificationKeys.includes(groupKey)) {
          return { daysBefore: override.daysBeforeToday, daysAfter: override.daysAfterToday };
        }
      }

      return defaultRange;
    },

    /**
     * Fetch and conditionally commit listings for an exact classification.
     * The optional query is reserved for the explicitly disabled legacy browse mode.
     * @param classificationKey Leaf classification to fetch.
     * @param epoch Cache epoch captured when the request began.
     * @param revision Classification revision captured when the request began.
     * @param searchText Optional legacy leaf-search query.
     * @returns Cached data, or null when the request fails or becomes stale.
     */
    async doFetchClassificationAds(
      classificationKey: number,
      epoch: number,
      revision: number,
      searchText = '',
    ): Promise<CachedClassificationAds | null> {
      if (!this.isLeafClassificationKey(classificationKey)) {
        console.warn(`[BrowseListings] Skipping internal listings request for non-leaf classification ${classificationKey}.`);
        return null;
      }

      try {
        console.log(`[BrowseListings] Fetching ads for classification ${classificationKey}...`);

        // Get effective date range (may be overridden for this classification's group)
        const dateRange = this.getEffectiveDateRange(classificationKey);

        // Determine if we should paginate
        const pageThreshold = getBrowsePageThreshold();
        const pageSize = getBrowsePageSize();
        const countInfo = this.counts.get(classificationKey);
        // The endpoint paginates regular classifieds only and returns display listings wholesale.
        // Using the combined count can make a display-only classification return early as empty.
        const regularAdCount = countInfo?.regularAdCount ?? 0;
        const shouldPaginate = regularAdCount > pageThreshold;
        const normalizedSearchText = searchText.trim();
        const isSearch = normalizedSearchText.length > 0;

        // Build request
        const request: Parameters<typeof classifiedOrderApi.getClassificationAds>[0] = {
          classificationKey,
          daysBeforeToday: dateRange.daysBefore,
          daysAfterToday: dateRange.daysAfter,
        };

        if (shouldPaginate || isSearch) {
          request.skip = 0;
          request.take = pageSize;
        }
        if (isSearch) {
          request.searchText = normalizedSearchText;
        }

        // Fetch ads and custom field definitions in parallel
        const [response, customFields] = await Promise.all([
          classifiedOrderApi.getClassificationAds(request),
          this.ensureCustomFieldsForClassification(classificationKey),
        ]);

        // Handle null/undefined response gracefully
        const regularAds = response?.scheduledAds || [];
        const displayAds = response?.displayAds || [];

        console.log(`[BrowseListings] Received ${regularAds.length} regular, ${displayAds.length} display ads, ${customFields.length} custom fields`);

        // An invalidation, count refresh, or clear occurred while the request was in flight.
        if (!isCurrentCacheRequest(classificationKey, epoch, revision)) {
          return null;
        }

        // Update pagination state
        if (response?.totalCount != null && response.totalCount > 0) {
          const loaded = regularAds.length;
          this.paginationState.set(classificationKey, {
            totalCount: response.totalCount,
            loadedCount: loaded,
            hasMore: loaded < response.totalCount,
          });
        } else {
          this.paginationState.delete(classificationKey);
        }

        // Cache the ads
        const cachedData: CachedClassificationAds = {
          classificationKey,
          regularAds,
          displayAds,
          searchText: normalizedSearchText,
          fetchedAt: Date.now(),
        };
        this.adCache.set(classificationKey, cachedData);

        return cachedData;

      } catch (err) {
        console.error(`Failed to fetch ads for classification ${classificationKey}:`, err);
        return null;
      }
    },

    /**
     * Get ads for a classification from cache only (synchronous).
     * Returns null if not in cache.
     */
    getAdsForClassification(classificationKey: number): ClassificationWithAds | null {
      if (!this.isLeafClassificationKey(classificationKey)) return null;
      const cached = this.adCache.get(classificationKey);
      if (!cached) return null;
      
      const classInfo = this.classificationMap.get(classificationKey);
      return {
        classificationKey,
        name: classInfo?.info.name || `Classification ${classificationKey}`,
        displayAds: cached.displayAds,
        regularAds: cached.regularAds,
        totalAdCount: cached.regularAds.length + cached.displayAds.length,
      };
    },

    /**
     * Load more ads for a classification (pagination "Load More" button).
     * Appends new ads to the existing cached ads.
     */
    async loadMoreAds(classificationKey: number): Promise<void> {
      if (!this.isLeafClassificationKey(classificationKey)) {
        console.warn(`[BrowseListings] Skipping pagination for non-leaf classification ${classificationKey}.`);
        return;
      }

      const pagination = this.paginationState.get(classificationKey);
      if (!pagination || !pagination.hasMore) return;

      const legacySearchText = this.currentSearchText.trim();
      const cachedAtStart = this.adCache.get(classificationKey);
      if (!cachedAtStart || (cachedAtStart.searchText ?? '') !== legacySearchText) {
        console.warn(`[BrowseListings] Skipping pagination for classification ${classificationKey} because its cache scope changed.`);
        return;
      }

      const dateRange = this.getEffectiveDateRange(classificationKey);
      const pageSize = getBrowsePageSize();
      const epoch = cacheEpoch;
      const revision = getCacheRevision(classificationKey);
      const skip = pagination.loadedCount;
      const existing = pendingPaginationFetches.get(classificationKey);
      if (existing
        && existing.epoch === epoch
        && existing.revision === revision
        && existing.searchText === legacySearchText
        && existing.skip === skip) {
        await existing.promise;
        return;
      }
      if (existing) {
        pendingPaginationFetches.delete(classificationKey);
      }

      const loadPromise = (async () => {
        try {
          const response = await classifiedOrderApi.getClassificationAds({
            classificationKey,
            daysBeforeToday: dateRange.daysBefore,
            daysAfterToday: dateRange.daysAfter,
            skip,
            take: pageSize,
            searchText: legacySearchText || undefined,
          });

          const newAds = response?.scheduledAds || [];
          console.log(`[BrowseListings] Loaded ${newAds.length} more ads for classification ${classificationKey}`);

          if (!isCurrentCacheRequest(classificationKey, epoch, revision)
            || this.currentSearchText.trim() !== legacySearchText) {
            return;
          }

          // Append only if the same page window is still current.
          const currentPagination = this.paginationState.get(classificationKey);
          const cached = this.adCache.get(classificationKey);
          if (!currentPagination
            || currentPagination.loadedCount !== skip
            || !cached
            || (cached.searchText ?? '') !== legacySearchText) return;
          cached.regularAds = [...cached.regularAds, ...newAds];
          this.adCache.set(classificationKey, { ...cached });

          // Update pagination state
          const newLoaded = skip + newAds.length;
          const totalCount = response?.totalCount ?? currentPagination.totalCount;
          this.paginationState.set(classificationKey, {
            totalCount,
            loadedCount: newLoaded,
            hasMore: newLoaded < totalCount,
          });
        } catch (err) {
          console.error(`Failed to load more ads for classification ${classificationKey}:`, err);
        }
      })();

      pendingPaginationFetches.set(classificationKey, {
        promise: loadPromise,
        epoch,
        revision,
        searchText: legacySearchText,
        skip,
      });

      try {
        await loadPromise;
      } finally {
        const current = pendingPaginationFetches.get(classificationKey);
        if (current?.promise === loadPromise) {
          pendingPaginationFetches.delete(classificationKey);
        }
      }
    },

    /**
     * Fetch one query-scoped page without mutating canonical listings or pagination.
     * The caller owns result aggregation and generation checks; request failures are thrown.
     * @param classificationKey Leaf classification to search.
     * @param searchText Nonempty query text. Leading and trailing whitespace is ignored.
     * @param skip Number of matching regular listings to skip.
     * @param take Maximum number of matching regular listings to return.
     * @returns A page of matching regular listings and its pagination metadata.
     */
    async fetchClassificationSearchPage(
      classificationKey: number,
      searchText: string,
      skip = 0,
      take = getBrowsePageSize(),
    ): Promise<ClassificationSearchPageResult> {
      const trimmedSearchText = searchText.trim();
      const normalizedSkip = Math.max(0, Math.trunc(skip));
      const normalizedTake = Math.max(1, Math.trunc(take));

      if (!this.isLeafClassificationKey(classificationKey)) {
        console.warn(`[BrowseListings] Skipping search for non-leaf classification ${classificationKey}.`);
        return {
          classificationKey,
          searchText: trimmedSearchText,
          regularAds: [],
          totalCount: 0,
          skip: normalizedSkip,
          take: normalizedTake,
          nextSkip: normalizedSkip,
          hasMore: false,
        };
      }

      if (!trimmedSearchText) {
        throw new Error('Search text is required.');
      }

      const dateRange = this.getEffectiveDateRange(classificationKey);

      try {
        const [response] = await Promise.all([
          classifiedOrderApi.getClassificationAds({
            classificationKey,
            daysBeforeToday: dateRange.daysBefore,
            daysAfterToday: dateRange.daysAfter,
            skip: normalizedSkip,
            take: normalizedTake,
            searchText: trimmedSearchText,
          }),
          this.ensureCustomFieldsForClassification(classificationKey),
        ]);

        if (!response) {
          throw new Error(`No search response was returned for classification ${classificationKey}.`);
        }

        const regularAds = response.scheduledAds || [];
        const loadedThrough = normalizedSkip + regularAds.length;
        const totalCount = Math.max(response.totalCount ?? 0, loadedThrough);
        const nextSkip = response.totalCount == null
          ? loadedThrough
          : Math.min(totalCount, normalizedSkip + normalizedTake);

        return {
          classificationKey,
          searchText: trimmedSearchText,
          regularAds,
          totalCount,
          skip: normalizedSkip,
          take: normalizedTake,
          nextSkip,
          hasMore: nextSkip < totalCount,
        };
      } catch (err) {
        console.error(`Failed to search classification ${classificationKey}:`, err);
        throw err;
      }
    },

    /**
     * Backwards-compatible first-page search wrapper.
     * Search results remain transient and never replace the canonical unfiltered cache.
     * @param classificationKey Leaf classification to search.
     * @param searchText Query text, or empty text to restore canonical listings.
     * @returns First-page search results, canonical listings when cleared, or null on failure.
     */
    async searchAds(classificationKey: number, searchText: string): Promise<ClassificationWithAds | null> {
      if (!this.isLeafClassificationKey(classificationKey)) {
        console.warn(`[BrowseListings] Skipping search wrapper for non-leaf classification ${classificationKey}.`);
        return null;
      }

      const trimmedSearchText = searchText.trim();
      this.currentSearchText = trimmedSearchText;
      this.isSearching = true;
      this.loadingClassificationKey = classificationKey;

      try {
        if (!trimmedSearchText) {
          return await this.fetchAdsForClassification(classificationKey, false);
        }

        const result = await this.fetchClassificationSearchPage(classificationKey, trimmedSearchText);
        const classInfo = this.classificationMap.get(classificationKey);
        return {
          classificationKey,
          name: classInfo?.info.name || `Classification ${classificationKey}`,
          displayAds: [],
          regularAds: result.regularAds,
          totalAdCount: result.regularAds.length,
        };
      } catch {
        return null;
      } finally {
        if (this.loadingClassificationKey === classificationKey) {
          this.loadingClassificationKey = null;
        }
        this.isSearching = false;
      }
    },

    /**
     * Execute the original cache-backed leaf search used when combined overviews are explicitly disabled.
     * Each query invalidates the prior canonical/search entry, and clearing the query performs a fresh
     * unfiltered request. Query-scoped pagination is retained through currentSearchText for loadMoreAds.
     * @param classificationKey Exact leaf classification to search.
     * @param searchText Search query, or empty text to restore unfiltered listings.
     * @returns The freshly loaded legacy search/unfiltered result, or null on failure or invalidation.
     */
    async searchAdsLegacy(classificationKey: number, searchText: string): Promise<ClassificationWithAds | null> {
      if (!this.isLeafClassificationKey(classificationKey)) {
        console.warn(`[BrowseListings] Skipping legacy search for non-leaf classification ${classificationKey}.`);
        return null;
      }

      const trimmedSearchText = searchText.trim();
      this.invalidateClassificationCache(classificationKey);

      const epoch = cacheEpoch;
      const revision = getCacheRevision(classificationKey);
      this.currentSearchText = trimmedSearchText;
      this.isSearching = true;
      this.loadingClassificationKey = classificationKey;

      const fetchPromise = this.doFetchClassificationAds(
        classificationKey,
        epoch,
        revision,
        trimmedSearchText,
      );
      pendingFetches.set(classificationKey, {
        promise: fetchPromise,
        epoch,
        revision,
        searchText: trimmedSearchText,
      });

      try {
        const result = await fetchPromise;
        if (!result || !isCurrentCacheRequest(classificationKey, epoch, revision)) {
          return null;
        }

        const classInfo = this.classificationMap.get(classificationKey);
        return {
          classificationKey,
          name: classInfo?.info.name || `Classification ${classificationKey}`,
          displayAds: result.displayAds,
          regularAds: result.regularAds,
          totalAdCount: result.regularAds.length + result.displayAds.length,
        };
      } finally {
        const current = pendingFetches.get(classificationKey);
        const ownsCurrentRequest = current?.promise === fetchPromise;
        if (ownsCurrentRequest) {
          pendingFetches.delete(classificationKey);
        }
        if (ownsCurrentRequest && this.loadingClassificationKey === classificationKey) {
          this.loadingClassificationKey = null;
          this.isSearching = false;
        }
      }
    },

    /**
     * Backwards compatible alias - calls fetchCounts
     */
    async fetchAds() {
      await this.fetchCounts();
    },

    /**
     * Start background prefetching of classifications with the most ads.
     * Call this after the initial classification is displayed to the user.
     * Prefetches one at a time to avoid overwhelming the server.
     * @param excludeKey Classification key to exclude (usually the currently viewed one)
     * @param maxToPrefetch Maximum number of classifications to prefetch (default 5)
     */
    startBackgroundPrefetch(excludeKey?: number, maxToPrefetch = 5) {
      if (this.isPrefetching) {
        console.log('[BrowseListings] Prefetching already in progress');
        return;
      }

      // Build list of classifications to prefetch, sorted by ad count (largest first)
      const classificationsToPrefetch: { key: number; count: number }[] = [];
      
      for (const [key, count] of this.counts) {
        // Counts are derived from scheduled data and can contain legacy/misassigned
        // group keys. Only exact tree leaves may reach the classification endpoint.
        if (!this.isLeafClassificationKey(key)
          || this.adCache.has(key)
          || key === excludeKey) continue;
        
        const totalCount = count.regularAdCount + count.displayAdCount;
        if (totalCount > 0) {
          classificationsToPrefetch.push({ key, count: totalCount });
        }
      }

      // Sort by count descending (largest first - they take longest to load)
      classificationsToPrefetch.sort((a, b) => b.count - a.count);

      // Take top N
      this.prefetchQueue = classificationsToPrefetch
        .slice(0, maxToPrefetch)
        .map(c => c.key);

      if (this.prefetchQueue.length === 0) {
        console.log('[BrowseListings] No classifications to prefetch');
        return;
      }

      this.prefetchGeneration += 1;
      const generation = this.prefetchGeneration;
      this.isPrefetching = true;

      console.log(`[BrowseListings] Starting background prefetch for ${this.prefetchQueue.length} classifications:`, 
        this.prefetchQueue.map(k => {
          const c = this.counts.get(k);
          return `${k} (${(c?.regularAdCount || 0) + (c?.displayAdCount || 0)} ads)`;
        }));

      this.processNextPrefetch(generation);
    },

    /**
     * Process the next item in the prefetch queue.
     * Called internally after each prefetch completes.
     */
    async processNextPrefetch(generation: number) {
      if (generation !== this.prefetchGeneration) {
        return;
      }

      if (this.prefetchQueue.length === 0) {
        this.isPrefetching = false;
        console.log('[BrowseListings] Background prefetch complete');
        return;
      }

      // Don't prefetch if user is actively loading a classification
      if (this.loadingClassificationKey !== null) {
        // Wait a bit and try again
        setTimeout(() => this.processNextPrefetch(generation), 1000);
        return;
      }

      const nextKey = this.prefetchQueue.shift()!;

      if (!this.isLeafClassificationKey(nextKey)) {
        console.warn(`[BrowseListings] Dropping non-leaf classification ${nextKey} from the prefetch queue.`);
        this.processNextPrefetch(generation);
        return;
      }

      // Skip if already cached (might have been loaded by user while waiting)
      if (this.adCache.has(nextKey)) {
        this.processNextPrefetch(generation);
        return;
      }

      // Use the shared canonical fetch mechanism so compatible foreground work is reused.
      console.log(`[BrowseListings] Prefetching classification ${nextKey}...`);
      try {
        const result = await this.fetchAdsForClassification(nextKey, false);
        if (result) {
          console.log(`[BrowseListings] Prefetched ${result.regularAds.length} regular, ${result.displayAds.length} display ads for classification ${nextKey}`);
        }
      } catch (err) {
        console.warn(`[BrowseListings] Failed to prefetch classification ${nextKey}:`, err);
        // Don't set error state for background prefetch failures
      }

      if (generation !== this.prefetchGeneration) return;

      // Small delay between prefetches to be nice to the server
      setTimeout(() => this.processNextPrefetch(generation), 500);
    },

    /**
     * Stop any ongoing background prefetch
     */
    stopPrefetch() {
      this.prefetchGeneration += 1;
      this.prefetchQueue = [];
      this.isPrefetching = false;
    },

    /**
     * Stop scheduling queued background prefetch work.
     * An already-running request may finish and populate the canonical cache.
     */
    stopBackgroundPrefetch() {
      this.stopPrefetch();
    },

    /**
     * Clear all data
     */
    clearData() {
      countsGeneration += 1;
      cacheEpoch += 1;
      cacheRevisions.clear();
      this.classificationTree = null;
      this.counts = new Map<number, ClassificationAdCount>();
      this.adCache = new Map<number, CachedClassificationAds>();
      this.customFieldsCache = new Map<number, CustomFieldInfo[]>();
      this.paginationState = new Map();
      this.currentSearchText = '';
      this.isSearching = false;
      this.loading = false;
      this.error = null;
      this.lastFetchDate = null;
      this.loadingClassificationKey = null;
      this.stopPrefetch();
      pendingFetches.clear();
      pendingCustomFieldFetches.clear();
      pendingPaginationFetches.clear();
    },
  },
});
